import styled, { css } from 'styled-components/macro'
import { darken, lighten } from 'polished'

const btnSize = 20

// Flowhub Design System hexes (literals required: polished darken/lighten
// cannot operate on CSS custom properties). On = action-primary deep-purple,
// off = gray-500 (text-muted). Border is a darkened shade of the track.
//
// H4 THEME NOTE: the track palette stays a matched literal set (the polished
// darken/lighten derivations need real hex inputs, and the on/off/knob/label
// colors move together so there is no mixed pair). It renders in the pearl
// brand hues across all themes. The on-state purple→moss flip is the separate
// Buttons/controls Decision #5 (docs/SHARED_FRONTEND_PROGRAM.md §3.0) — a value
// change, out of scope for this pearl-identical pass.
const DS_ON_COLOR = '#20026c' // --action-primary (deep-purple); LITERAL: fed to polished darken/lighten
const DS_OFF_COLOR = '#6f7686' // --gray-500

interface ColorSchemeProps {
  disabled?: boolean
  on?: string
}
const colorScheme = ({ disabled, on }: ColorSchemeProps) => {
  const thisColor = on ? DS_ON_COLOR : DS_OFF_COLOR
  const borderColor = darken(0.15, thisColor)

  return css`
    background-color: ${disabled ? lighten(0.16, thisColor) : thisColor};
    border-color: ${disabled ? lighten(0.15, borderColor) : borderColor};
  `
}

const contentText = (props: ColorSchemeProps) =>
  props.on
    ? css`
        content: 'on';
      `
    : css`
        content: 'off';
        transform: translateX(${btnSize}px);
      `

export const Label = styled.label`
  display: block;
`

export const ToggleBtn = styled.span<{
  btnSize?: number
  disabled?: boolean
  on?: string
}>`
  border: 1px solid transparent;
  border-radius: var(--mantine-radius-lg);
  cursor: ${({ disabled }) => (disabled ? 'inherit' : 'pointer')};
  display: inline-flex;
  height: 26px;
  outline: 0;
  position: relative;
  transition: all 0.4s ease;
  user-select: none;
  width: 56px;
  ${colorScheme};

  &:after {
    background: var(--white);
    border-radius: 50%;
    content: '';
    display: block;
    height: ${btnSize}px;
    left: ${({ on }) => (on ? 'calc(100% - ' + (btnSize + 2) + 'px)' : '2px')};
    position: relative;
    top: 2px;
    transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275),
      padding 0.3s ease, margin 0.3s ease;
    width: ${btnSize}px;
    ${({ disabled }) =>
      !disabled &&
      css`
        box-shadow: 0 0 0 2px rgba(20, 2, 76, 0.12),
          0 4px 0 rgba(20, 2, 76, 0.12);
      `};
  }

  &:before {
    align-items: center;
    color: var(--white);
    display: flex;
    font-size: var(--mantine-font-size-2xs);
    font-weight: var(--font-weight-thin);
    height: 100%;
    justify-content: center;
    left: 0px;
    letter-spacing: var(--letter-spacing-wide);
    padding: 0 2px;
    position: absolute;
    text-align: center;

    text-transform: uppercase;
    width: calc(100% - ${btnSize + 2}px);
    ${contentText};
    ${({ disabled }) =>
      !disabled &&
      css`
        text-shadow: 1px 1px 0 rgba(20, 2, 76, 0.12);
      `};
  }
`

export const ToggleInput = styled.input`
  /* visually hidden but still accessible */
  border: 0;
  clip: rect(0 0 0 0);
  cursor: inherit;
  height: 1px;
  margin: -1px;
  outline: none;
  overflow: hidden;
  padding: 0;
  position: absolute;
  width: 1px;
  white-space: nowrap;
`

export const Wrap = styled.div<{ disabled?: boolean; hasFocus?: boolean }>`
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'default')};
  outline: none;

  ${({ hasFocus }) =>
    hasFocus &&
    css`
      outline: 5px auto -webkit-focus-ring-color;
      outline-offset: 4px;
    `};
`
